import * as React from "react";
import { Search } from "lucide-react";
import { Combobox, Input, InputGroup } from "@/components/ui";
import { ContextType } from "@/services/bedrock/dtos/DesignPackage";
import { useDebounced } from "@/utils/useDebounced";
import { CONTEXT_TYPE_LABELS } from "../packageStatus";

export type PackageStatusFilter = "all" | "published" | "draft" | "failed" | "archived";

export const PACKAGE_STATUS_FILTERS: PackageStatusFilter[] = ["all", "published", "draft", "failed", "archived"];

const STATUS_LABELS: Record<PackageStatusFilter, string> = {
  all: "Any status",
  published: "Published",
  draft: "Drafts",
  failed: "Failed",
  archived: "Archived",
};

export interface PackageListToolbarProps {
  q: string;
  onQChange(q: string): void;
  contextType: ContextType | null;
  onContextTypeChange(v: ContextType | null): void;
  status: PackageStatusFilter;
  onStatusChange(v: PackageStatusFilter): void;
  /** Admin views: the scope itself is picked in the section header. */
  adminScope?: { scope: "global" | "coach" };
  canEdit: boolean;
}

interface Option<V> {
  value: V;
  label: string;
}

const TYPE_OPTIONS: Option<ContextType | null>[] = [
  { value: null, label: "All types" },
  { value: "review", label: CONTEXT_TYPE_LABELS.review },
  { value: "player", label: CONTEXT_TYPE_LABELS.player },
  { value: "none", label: CONTEXT_TYPE_LABELS.none },
];

export const TypeFilter: React.FC<{ value: ContextType | null; onChange(v: ContextType | null): void }> = ({
  value,
  onChange,
}) => (
  <Combobox
    items={TYPE_OPTIONS}
    value={TYPE_OPTIONS.find(option => option.value === value) ?? TYPE_OPTIONS[0]}
    isItemEqualToValue={(option, selected) => option.value === selected?.value}
    onValueChange={option => onChange(option?.value ?? null)}
    placeholder="All types"
    className="max-w-44 min-w-40"
  />
);

const SEARCH_DEBOUNCE_MS = 400;

export const PackageListToolbar: React.FC<PackageListToolbarProps> = ({
  q,
  onQChange,
  contextType,
  onContextTypeChange,
  status,
  onStatusChange,
  adminScope,
  canEdit,
}) => {
  const [search, setSearch] = React.useState(q);
  const debounced = useDebounced(search, SEARCH_DEBOUNCE_MS);
  const onQChangeRef = React.useRef(onQChange);
  onQChangeRef.current = onQChange;

  // The last value this box wrote to the URL; any other change to `q` came from outside.
  const lastWritten = React.useRef(q);

  // External resets (Clear filters, scope switch) replace the text in the box, which also
  // cancels a pending debounced write. Our own trimmed writes leave the box as typed.
  React.useEffect(() => {
    if (q === lastWritten.current) return;
    lastWritten.current = q;
    setSearch(q);
  }, [q]);

  React.useEffect(() => {
    const next = debounced.trim();
    if (next === q) return;
    lastWritten.current = next;
    onQChangeRef.current(next);
  }, [debounced]);

  const archivedAllowed = canEdit && adminScope?.scope !== "global";
  const statusOptions: Option<PackageStatusFilter>[] = PACKAGE_STATUS_FILTERS.filter(
    value => value !== "archived" || archivedAllowed
  ).map(value => ({ value, label: STATUS_LABELS[value] }));

  return (
    <div className="flex items-center flex-wrap justify-between gap-4">
      <div className="flex gap-3 items-center flex-wrap flex-auto">
        <InputGroup
          className="max-w-80 w-auto min-w-50"
          control={controlProps => (
            <Input
              {...controlProps}
              value={search}
              placeholder="Search designs…"
              onChange={event => setSearch(event.target.value)}
            />
          )}
          append={<Search />}
        />
        <TypeFilter value={contextType} onChange={onContextTypeChange} />
        <Combobox
          items={statusOptions}
          value={statusOptions.find(option => option.value === status) ?? statusOptions[0]}
          isItemEqualToValue={(option, selected) => option.value === selected?.value}
          onValueChange={option => onStatusChange(option?.value ?? "all")}
          placeholder="Any status"
          className="max-w-44 min-w-40"
        />
      </div>
      <span className="text-xs text-slate-500">Sorted by recently edited</span>
    </div>
  );
};
